import { Dialog, DialogActions, DialogContent, DialogTitle, Typography, Button } from '@mui/material';
import { Box } from '@mui/system';
import { useEffect, useState } from 'react';
import ContactPersonnelList from '../ContactPersonnelList';
import {
  getCommunityMembers,
  getExperts,
  getJournalists,
  mergeCommunityMembers,
  mergeExperts,
  mergeJournalists,
} from '../contactsRequest';
import MergeIcon from '@mui/icons-material/MergeType';
import { useDispatch } from 'react-redux';
import { openSnackbar } from '../../AppSnackbar/snackbarSlice';
import ContactFuzzySearch from '../ContactFuzzySearch';
import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query';

function MergeContactPersonnelModal(props) {
  const { units } = props;
  const { category } = props;
  const { openMergeContact, setOpenMergeContact, selectedContact, setRefreshContacts, setOpenAddContact } = props;
  const [contacts, setContacts] = useState([]);
  const [mergingContacts, setMergingContacts] = useState([]);
  const [query, setQuery] = useState('');

  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const handleClose = (backToContactPage) => {
    setOpenMergeContact(false);
    if (backToContactPage) setOpenAddContact(false);
  };

  const contactsQuery = useQuery({
    queryKey: ['mergeContactPersonnel', category],
    queryFn: () => {
      switch (category) {
        case 'Journalists':
          return getJournalists();
        case 'Experts':
          return getExperts();
        case 'Community Members':
          return getCommunityMembers();
        default:
          return getJournalists();
      }
    },
  });

  const mergeMutation = useMutation({
    mutationFn: () => {
      switch (category) {
        case 'Journalists':
          return mergeJournalists(mergingContacts, selectedContact);
        case 'Experts':
          return mergeExperts(mergingContacts, selectedContact);
        case 'Community Members':
          return mergeCommunityMembers(mergingContacts, selectedContact);
        default:
          return mergeJournalists(mergingContacts, selectedContact);
      }
    },
    onSuccess: async (res) => {
      if (res.status === 200) {
        dispatch(openSnackbar({ severity: 'success', message: 'Contact Personnel Merged!' }));
        handleClose(true);
        setRefreshContacts(true);
        await queryClient.invalidateQueries({ queryKey: ['dropdown-table'] });
      } else {
        dispatch(openSnackbar({ severity: 'error', message: 'Contact Personnel Merge Failed!' }));
      }
    },
    onError: () => {
      dispatch(openSnackbar({ severity: 'error', message: 'Contact Personnel Merge Failed!' }));
    },
  });

  const handleMerge = () => {
    mergeMutation.mutate();
  };

  useEffect(() => {
    let responseData = [];
    if (Array.isArray(contactsQuery.data?.data)) {
      responseData = contactsQuery.data.data;
    }
    setContacts(responseData);
  }, [contactsQuery.data]);

  return (
    <Dialog
      open={openMergeContact}
      onClose={handleClose}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      }}
      maxWidth="sm"
      fullWidth={true}
      data-cy="dialog-MergeContactPersonnelModal"
    >
      <DialogTitle>Merge Contact</DialogTitle>
      <DialogContent sx={{ overflowX: 'hidden' }}>
        <Typography variant="body1">
          {'Please select which contact(s) to merge into:'}
          <strong> {selectedContact?.name}</strong>
        </Typography>
        <Typography variant="body1">
          Selected to become above:
          <strong>{mergingContacts.map((contact) => contact.name).join()}</strong>
        </Typography>
        <Box
          sx={{
            marginBottom: '10px',
            marginTop: '10px',
            width: '35rem',
          }}
        >
          <ContactFuzzySearch
            contacts={contacts}
            setSearchResults={setContacts}
            query={query}
            setQuery={setQuery}
            category={category}
            units={units}
            fullWidth={true}
          />
        </Box>
        <Box
          sx={{
            height: '50vh',
          }}
        >
          <ContactPersonnelList
            data={contacts}
            category={category}
            units={units}
            isMerging={true}
            selectedContact={selectedContact}
            mergingContacts={mergingContacts}
            setMergingContacts={setMergingContacts}
          />
        </Box>
        <DialogActions>
          <br></br>
          <Button onClick={handleClose} variant="contained" data-cy="cancel-button-MergeContactPersonnelModal">
            Cancel
          </Button>
          <Button
            onClick={handleMerge}
            startIcon={<MergeIcon />}
            variant="contained"
            data-cy="merge-button-MergeContactPersonnelModal"
          >
            Merge
          </Button>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
}

export default MergeContactPersonnelModal;
